<template>
  <div>
    <loading-cube v-if="loading"/>
    <div v-if="!loading">
      <AdminModeContext v-slot="{ adminActive, adminBusy, toggleAdmin, withAdminLock }">
        <PageHeading
            v-if="workerData"
            :back="{ name: 'a_timesheet_r2_select_site_worker', params: {company_id, start_date, end_date, order_id, order_job_id}, query: {timesheet: timesheet_id} }"
        >
          <template #title>
            <router-link :to="{name: 'company_record', query: {company_id}}">
              {{ company?.company_name }}
            </router-link>
            <br/>
            <router-link :to="{ name: 'a_timesheet_r2_select_sites', params: {company_id, start_date, end_date} }">Submit Sites</router-link>
            ->
            <router-link
                :to="{ name: 'a_timesheet_r2_select_site_worker', params: {company_id, start_date, end_date, order_id, order_job_id} }">
              Submit Timesheets
            </router-link>
            ->
            Submit Timesheet
          </template>

          <template>
            <h2>
              <router-link :to="{name: 'worker_profile', query:{timesheet: timesheet_id}}">
                {{ ` ${workerData.first_name} ${workerData.surname} - ${workerData.job_title}` }}
              </router-link>
            </h2>
            <p v-if="workerData?.rate">
              Rate: <Rate :data="workerData.rate"/>
            </p>

            <p>
              Selected Timesheet period: {{ dateFormat(period) }}.
              <router-link :to="{name: 'a_timesheet_r2_select_period', params: {company_id}}">(change)</router-link>
            </p>
          </template>
        </PageHeading>

        <div class="pane admin_bar">
          <AdminToggleButton
              :active="adminActive"
              :toggle-admin="toggleAdmin"
              :disabled="adminBusy"
          />

          <!-- Timesheet level admin actions, mirroring the worker rows. Both render as Force
               variants for holders of sunset_timesheet_force_actions, soft for everyone else. -->
          <div class="header-panel" v-if="adminActive && isGod()">
            <label class="filter-label">Admin actions</label>
            <div class="filter-panel__admin-actions">
              <AdminReopenTimesheetButton
                  :company-id="company_id"
                  :timesheet-id="timesheet_id"
                  :admin-busy="adminBusy"
                  :with-admin-lock="withAdminLock"
                  :success="onAdminReset"
              />
              <AdminResetTimesheetButton
                  :company-id="company_id"
                  :timesheet-id="timesheet_id"
                  :admin-busy="adminBusy"
                  :with-admin-lock="withAdminLock"
                  :success="onAdminReset"
              />
            </div>
          </div>
        </div>

        <div class="pane">
          <timesheet_form_container
              :key="`ts-form-${resetToken}`"
              :company_id="company_id"
              :end_date="end_date"
              :order_id="order_id"
              :order_job_id="order_job_id"
              :start_date="start_date"
              :timesheet_id="timesheet_id"
          />
        </div>
      </AdminModeContext>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page_heading {
  padding-left: 1em;
  margin-bottom: 2em;
}

.pane {
  padding: 1em;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 10px;
}

.admin_bar {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  grid-column-gap: 2em;
  align-items: center;
  margin-bottom: 1em;
}

/* Admin actions panel - matches the equivalent panel on the select sites page. */
.header-panel {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 20px 1fr;
  justify-content: flex-start;
}

.filter-panel__admin-actions {
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 10px;
  grid-auto-columns: max-content;
  justify-content: flex-start;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #777;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
</style>

<script>
import timesheet_form_container from './components/timesheet_form_container.vue'
import loadingCube from "loaders/generic_record_loader.vue";
import PageHeading from "../components/shared/PageHeading.vue";
import {dateFormat, isGod} from '../components/shared/index';
import Rate from '../components/shared/Rate.vue';
import AdminModeContext from "pages/sunset/timesheets/components/shared/AdminModeContext.vue";
import AdminToggleButton from "pages/sunset/timesheets/components/shared/AdminToggleButton.vue";
import AdminResetTimesheetButton from "pages/sunset/timesheets/admin/components/AdminResetTimesheetButton.vue";
import AdminReopenTimesheetButton from "pages/sunset/timesheets/admin/components/AdminReopenTimesheetButton.vue";

export default {
  components: {
    PageHeading,
    loadingCube,
    timesheet_form_container,
    Rate,
    AdminModeContext,
    AdminToggleButton,
    AdminResetTimesheetButton,
    AdminReopenTimesheetButton
  },
  props: {
    company_id: {required: true},
    start_date: {required: true},
    end_date: {required: true},
    order_id: {required: true},
    order_job_id: {required: true},
    timesheet_id: {required: true},
  },
  data() {
    return {
      loading: false,
      error: null,
      period: this.end_date,
      workerData: null,
      company: null,
      site: null,
      resetToken: 0,
    }
  },
  async beforeMount() {
    await this.init();
  },
  methods: {
    dateFormat,

    isGod,

    /**
     * timesheet_form_container loads its own data on mount, so bumping the key is what
     * actually makes the reset visible - init() alone would leave the form showing stale values.
     */
    async onAdminReset() {
      this.resetToken += 1;
      await this.init();
    },

    async init() {
      this.loading = true;
      await this.fetchWorker();
      await this.fetchSite();
      await this.fetchSupportingData();
      this.loading = false;
    },

    async fetchSupportingData() {
      const result = await axios.get(this.crmApiPath + 'company/' + this.company_id);
      if (result.status !== 200) {
        console.error('Could not fetch company');
        return;
      }
      this.company = result.data;
    },

    async fetchWorker() {
      let result = await axios.get(`${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/order_job/${this.order_job_id}/workers/${this.timesheet_id}?submission_date=${this.period}`);
      if (result.status !== 200) {
        console.error('Could not fetch timesheet:', err);
        this.error = 'Failed to load timesheet data';
        return;
      }
      this.workerData = {...result.data.data};
    },

    async fetchSite() {
      const result = await axios.get(`${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/sites/${this.order_id}/${this.order_job_id}?submission_date=${this.period}`);

      if (result.status !== 200) {
        console.error('Could not fetch site');
        return;
      }
      this.site = {...result.data.data};
    },
  }
}
</script>
